import { InfoCircle } from "react-bootstrap-icons";
import React, { useEffect, useState } from "react";
import { FiMail } from "react-icons/fi";
import { Link } from "react-router-dom";
import { Button, Form, Image, Modal, OverlayTrigger, Spinner, Tooltip, } from "react-bootstrap";
import { getAddOnServicesList } from "../../services/Networkcall";
import { errorAlert } from "../common/Alert";
import InputField from "../common/InputField";

const ExtrasSection = ({
  locationDetail,
  bookingType,
  selectedAddOns,
  addOnServicesList,
  setAddOnServicesList,
  setConfirm,
}) => {

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [localSelectedAddOns, setLocalSelectedAddOns] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeServiceId, setActiveServiceId] = useState(null);

  // Derived (not its own useState) so it always reflects the latest
  // localSelectedAddOns — keeping activeService as a separate useState
  // meant it went stale as soon as the user typed, since typing only
  // updates localSelectedAddOns.
  const activeService = localSelectedAddOns.find((s) => s._id === activeServiceId) || null;

  // =======================================
  // FETCH LOCATION BASED ADDONSERVICE LIST
  // =======================================
  const getAddOnServices = async () => {
    setLoading(true);
    try {
      const res = await getAddOnServicesList({ locationId: locationDetail?._id, bookingType: bookingType });
      if (res.success) {
        const mData = res.data.map((s) => ({ ...s, isSelected: s.isFree ? true : false, }));
        setLocalSelectedAddOns(mData);
      } else {
        errorAlert({ message: res.message });
      }
    } catch (err) {
      errorAlert({ message: err?.response?.data?.message || err?.message || "something went wrong", });
    } finally {
      setLoading(false);
    }
  };

  // =======================================
  // ADDONSERVICE PRICE BASED ON BOOKING TYPE
  // =======================================
  const getServicePriceBasedOnBookingType = ({ service, bookingType }) => {
    let price = null;
    switch (bookingType) {
      case "DAY":
        price = service?.dayPrice?.price;
        break;
      case "WEEKLY":
        price = service?.weeklyPrice?.price;
        break;
      case "MONTHLY":
        price = service?.monthlyPrice?.price;
        break;
      case "MONTHLY_SUBSCRIPTION":
        price = service?.monthlySubscriptionPrice?.price;
        break;
      default:
        console.warn(`Unknown booking type: ${bookingType}`);
    }
    return price;
  };

  // =======================================
  // TOGGLE ADDON SERVICE (opens modal for third-party services)
  // =======================================
  const handleToggleAddOn = (id) => {
    const service = localSelectedAddOns.find((a) => a._id === id);
    if (!service) return;

    setLocalSelectedAddOns((prev) =>
      prev.map((addOn) =>
        addOn._id === id ? { ...addOn, isSelected: !service.isSelected } : addOn
      )
    );

    if (!service.isSelected && service.isThirdPartyService) {
      setActiveServiceId(id);
      setShowModal(true);
    }
  };

  // =======================================
  // ADDON SERVICE TOOLTIP DESCRIPTION
  // =======================================
  const renderTooltip = (props, name, description) => (
    <Tooltip id="button-tooltip" {...props} style={{ ...props.style, width: "350px" }}>
      <div className="text-start">
        <strong>{name}</strong>
        {description && <div className="mt-1">{description}</div>}
      </div>
    </Tooltip>
  );

  // =======================================
  // INPUT HANDLER (used inside the modal form)
  // =======================================
  const handleInputChange = (id, e) => {
    const { name, type, checked, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setLocalSelectedAddOns((prev) =>
      prev.map((item) => (item._id === id ? { ...item, [name]: newValue } : item))
    );

    setErrors((prev) => {
      if (!prev[id]) return prev;
      const { [name]: _removed, ...rest } = prev[id];
      return { ...prev, [id]: rest };
    });
  };

  // =======================================
  // VALIDATE ALL SELECTED THIRD PARTY ADDONS 
  // =======================================
  const validateThirdPartyServiceAddOn = () => {
    const selectedThirdPartyServices = localSelectedAddOns.filter(
      (s) => s.isThirdPartyService === true && s.isSelected
    );

    if (selectedThirdPartyServices.length === 0) {
      return true;
    }

    let isValid = true;
    const newErrors = {};

    selectedThirdPartyServices.forEach((service) => {
      const serviceErrors = {};
      if (!service.addon_description) {
        serviceErrors.addon_description = "Description is required.";
        isValid = false;
      }
      if (!service.addon_terms_agree) {
        serviceErrors.addon_terms_agree = "Please agree to Terms & agreement.";
        isValid = false;
      }
      if (Object.keys(serviceErrors).length > 0) {
        newErrors[service._id] = serviceErrors;
      }
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  // =======================================
  // MODAL OPEN / CLOSE HANDLERS
  // =======================================
  const handleOpenModal = (id) => {
    setActiveServiceId(id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setActiveServiceId(null);
  };

  const handleCancelModal = () => {
    if (activeServiceId) {
      setLocalSelectedAddOns((prev) =>
        prev.map((addOn) =>
          addOn._id === activeServiceId ? { ...addOn, isSelected: false } : addOn
        )
      );
      setErrors((prev) => {
        const { [activeServiceId]: _removed, ...rest } = prev;
        return rest;
      });
    }
    handleCloseModal();
  };

  const handleAddServiceModal = () => {
    if (validateThirdPartyServiceAddOn()) {
      handleCloseModal();
    }
  };

  // =======================================
  // EFFECTS
  // =======================================
  useEffect(() => {
    getAddOnServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationDetail, bookingType]);

  // =======================================
  // CONFIRM HANDLER
  // =======================================
  const handleConfirmClick = () => {
    if (validateThirdPartyServiceAddOn()) {
      setAddOnServicesList(localSelectedAddOns);
      setConfirm(true);
    }
  };

  return (
    <>
      <section className="card extras-card mt-3">
        <h4>
          Enhance Your Parking <span>(Optional)</span>
        </h4>

        {loading ? (
          <div className="d-flex py-5 justify-content-center align-items-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (localSelectedAddOns.map((service) => (
          <div key={service._id} className="extra-row">
            <div className="left">
              <Image src={service?.image || "assets/images/imageplaceholder.png"} width={50} height={50} className="object-fit-fill" />
              <div>
                <div className="extra-row-title">
                  {service.name} {service.serviceType && (<span className="tag ms-2">{service.serviceType}</span>)}
                </div>
                <small>{service.subtitle}</small>
              </div>
            </div>

            {/* RIGHT */}
            <div className="right flex-column align-items-end">
              <div className="price-box">
                <div className="price fw-bold">
                  <span className="text-primary fs-5">
                    {service?.isFree ? "Free" : `$ ${getServicePriceBasedOnBookingType({ service, bookingType })}`}
                  </span>
                  {!service?.isFree && <span>/ night</span>}
                </div>
              </div>

              <div className="price-box flex-row gap-0 align-items-center">
                <Form.Check
                  type="switch"
                  id={`custom-switch-${service?._id}`}
                  className="fs-3"
                  checked={service?.isFree || service?.isSelected}
                  onChange={() => !service?.isFree && handleToggleAddOn(service?._id)}
                />
                <div>
                  <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={(props) => renderTooltip(props, service?.name, service?.description)}
                  >
                    <InfoCircle className="info cursor-pointer ms-2" />
                  </OverlayTrigger>
                </div>
              </div>
              {service?.isThirdPartyService && service?.isSelected && (
                <Button variant="link" size="sm" className="p-0 ms-2 fw-bold text-decoration-none" onClick={() => handleOpenModal(service._id)}> Edit details </Button>
              )}
            </div>
          </div>
        ))
        )}

        <div className="extras-total">
          <Button variant="primary" size="sm" onClick={handleConfirmClick}>
            Confirm
          </Button>
        </div>
      </section>

      {/* THIRD-PARTY SERVICE DETAILS MODAL */}
      <Modal show={showModal} onHide={handleCancelModal} centered>
        <Modal.Body className="p-4 pb-0">
          {activeService && (
            <>
              <div className="d-flex align-items-start justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                  <Image src={activeService?.image || "assets/images/imageplaceholder.png"} width={56} height={56} className="rounded object-fit-fill" />
                  <div>
                    <div className="fs-4 fw-bold">{activeService?.name}</div>
                    {activeService?.serviceType && ( <span className="tag mt-1 d-inline-block"> {activeService.serviceType} </span> )}
                  </div>
                </div>
                <span className="addon-selected-badge rounded-circle d-flex align-items-center justify-content-center"> ✓  </span>
              </div>

              <div className="d-flex align-items-center gap-2 form-control bg-light mb-4">
                <FiMail color="#4E78EC" /> <span>{activeService?.thirdPartyProviderEmail || ""}</span>
              </div>

              <div className="mb-3">
                <Form.Label className="fw-bold mb-1"> Remarks <span className="text-muted fw-normal">(optional)</span> </Form.Label>
                <InputField
                  type="text" 
                  name="addon_remark"
                  value={activeService?.addon_remark || ""}
                  onChange={(e) => handleInputChange(activeService._id, e)}
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <Form.Label className="fw-bold mb-1"> Description <span className="text-danger">*</span></Form.Label>
                <InputField
                  as="textarea"
                  rows={3}
                  name="addon_description"
                  value={activeService?.addon_description || ""}
                  onChange={(e) => handleInputChange(activeService._id, e)}
                  error={errors[activeService._id]?.addon_description}
                />
              </div>

              <hr />

              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  name="addon_terms_agree"
                  checked={activeService.addon_terms_agree || false}
                  onChange={(e) => handleInputChange(activeService._id, e)}
                />
                <span> I agree to the <Link to={activeService?.termsAndAgreementFile?.url} target="_blank" className="text-decoration-underline fw-semibold text-primary" > Terms &amp; Conditions </Link> <span className="text-danger">*</span> </span>
              </div>
              {errors[activeService._id]?.addon_terms_agree && (
                <small className="text-danger d-block mb-2"> {errors[activeService._id]?.addon_terms_agree}</small>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 my-2">
          <div className="d-flex w-100 gap-3">
          <Button variant="outline-secondary" className="w-50 rounded-pill"  onClick={handleCancelModal} > Cancel </Button>
            <Button variant="primary" className="w-50 rounded-pill" onClick={handleAddServiceModal} > Add Service  </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default React.memo(ExtrasSection);